import {
  faCaretDown,
  faCaretUp,
  faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export type Option = {
  value: string;
  label: string;
};

type Props = {
  disabled?: boolean;
  loading?: boolean;
  minQueryLength?: number;
  multi?: boolean;
  onChange: (value: string[]) => void;
  onSearch?: (query: string) => void;
  options?: readonly Option[];
  placeholder?: string;
  value: string[];
  variant?: 'form' | 'filter';
};

export function SearchSelect({
  disabled = false,
  loading = false,
  minQueryLength = 0,
  multi = true,
  onChange,
  onSearch,
  options = [],
  placeholder,
  value,
  variant = 'form',
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAsync = onSearch !== undefined;
  const selected = options.filter((o) => value.includes(o.value));
  const visibleOptions = isAsync
    ? options
    : options.filter((o) =>
        o.label.toLowerCase().includes(filter.toLowerCase()),
      );
  const showSingleValue = !multi && selected.length > 0 && !open;
  const queryTooShort = isAsync && filter.length < minQueryLength;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (optionValue: string) => {
    if (multi) {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
      setFilter('');
      inputRef.current?.focus();
    } else {
      onChange([optionValue]);
      setFilter('');
      setOpen(false);
    }
  };

  return (
    <Container
      ref={containerRef}
      $disabled={disabled}
      $open={open}
      $variant={variant}
      onClick={() => {
        if (disabled) return;
        setOpen(true);
        inputRef.current?.focus();
      }}
    >
      <InputRow>
        {multi &&
          selected.map((opt) => (
            <Chip key={opt.value}>
              {opt.label}
              <ChipRemove
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((v) => v !== opt.value));
                }}
              >
                <FontAwesomeIcon icon={faCircleXmark} />
              </ChipRemove>
            </Chip>
          ))}
        {showSingleValue ? (
          <SingleValue>{selected[0]?.label}</SingleValue>
        ) : (
          !disabled && (
            <FilterInput
              ref={inputRef}
              value={filter}
              onChange={(e) => {
                const next = e.target.value;
                setFilter(next);
                setOpen(true);
                onSearch?.(next);
              }}
              onFocus={() => setOpen(true)}
              placeholder={selected.length === 0 ? placeholder : undefined}
            />
          )
        )}
      </InputRow>
      <CaretIcon>
        <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} />
      </CaretIcon>
      {!disabled && open && (
        <Dropdown>
          {queryTooShort ? (
            // Translation Needed
            <DropdownMessage>Please enter 3 or more characters</DropdownMessage>
          ) : loading ? (
            // Translation Needed
            <DropdownMessage>Searching...</DropdownMessage>
          ) : visibleOptions.length === 0 ? (
            <DropdownMessage>{t('wbs.no-results-found')}</DropdownMessage>
          ) : (
            visibleOptions.map((opt) => {
              const isSelected = value.includes(opt.value);
              return (
                <DropdownOption
                  key={opt.value}
                  type="button"
                  $selected={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleOption(opt.value);
                  }}
                >
                  {opt.label}
                </DropdownOption>
              );
            })
          )}
        </Dropdown>
      )}
    </Container>
  );
}

const Container = styled.div<{
  $disabled: boolean;
  $open: boolean;
  $variant: 'form' | 'filter';
}>`
  background-color: ${({ $variant }) =>
    $variant === 'filter' ? 'var(--white)' : 'var(--grey-75)'};
  border-radius: ${({ $open }) => ($open ? '6px 6px 0 0' : '6px')};
  box-shadow: var(--shadow-inset-sm);
  box-sizing: border-box;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
  min-height: 30px;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  padding: 2px 40px 2px 8px;
  position: relative;
  width: 100%;
`;

const InputRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 24px;
`;

const Chip = styled.span`
  align-items: center;
  background: var(--wbs-red);
  border-radius: 4px;
  color: var(--white);
  display: inline-flex;
  font-family: var(--raleway);
  font-size: 12px;
  gap: 8px;
  padding: 3px 4px;
`;

const ChipRemove = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  justify-content: center;
  padding: 0;
`;

const SingleValue = styled.span`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 13px;
  padding: 2px 0;
`;

const DropdownMessage = styled.div`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 12px;
  font-style: italic;
  padding: 10px 8px;
`;

const FilterInput = styled.input`
  background: transparent;
  border: none;
  color: var(--grey-900);
  flex: 1 1 60px;
  font-family: var(--raleway);
  font-size: 13px;
  min-width: 60px;
  outline: none;
  padding: 2px 0;

  &::placeholder {
    color: var(--grey-600);
  }
`;

const CaretIcon = styled.span`
  align-items: center;
  background: inherit;
  border-radius: 0 6px 6px 0;
  box-shadow: var(--shadow-inset-sm);
  color: var(--grey-600);
  display: flex;
  font-size: 16px;
  height: 100%;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  width: 32px;
`;

const Dropdown = styled.div`
  background: var(--white);
  border-radius: 0 0 6px 6px;
  box-shadow: var(--shadow-inset-lg);
  left: 0;
  max-height: 240px;
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 10;
`;

const DropdownOption = styled.button<{ $selected: boolean }>`
  background: none;
  border: none;
  color: ${({ $selected }) =>
    $selected ? 'var(--grey-500)' : 'var(--grey-900)'};
  cursor: pointer;
  font-family: var(--raleway);
  font-size: 12px;
  padding: 10px 8px;
  text-align: left;
  width: 100%;

  &:hover {
    background: var(--wbs-red);
    color: ${({ $selected }) =>
      $selected ? 'var(--grey-500)' : 'var(--grey-200)'};
  }
`;
